import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

export function IssuanceProviderTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [smokeBusy, setSmokeBusy] = useState(false);
  const [smokeResult, setSmokeResult] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/issuance-provider/status");
      setData(r.data);
    } catch {
      // Silently hide — endpoint requires admin; main page handles auth.
    }
  };

  useEffect(() => { refresh(); }, []);

  if (!data) return null;

  const dotColor = (status) => {
    if (status === "stripe" && data.healthcheck?.reachable === true) return "bg-emerald-500";
    if (status === "stripe" && data.healthcheck?.reachable === false) return "bg-rose-500";
    if (status === "mock") return "bg-[#CBA35C]";
    return "bg-stone-400";
  };

  const switchProvider = async (provider) => {
    if (provider === data.active_provider) return;
    if (!window.confirm(`Switch issuance provider to '${provider}'?\n\nThis affects ALL new card issuances until the next pod restart (env var change resets it).`)) return;
    setBusy(true);
    try {
      await api.put("/admin/issuance-provider/switch", { provider });
      toast.success(`Switched to ${provider}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  const runSmokeTest = async () => {
    setSmokeBusy(true);
    setSmokeResult(null);
    try {
      const r = await api.post("/admin/issuance-provider/smoke-test", {
        cardholder_name: "Smoke Test",
        amount_usd: 1.00,
      });
      setSmokeResult({ ok: true, ...r.data });
      toast.success(`Smoke test passed: ${r.data.provider} → ${r.data.last4}`);
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Smoke test failed";
      setSmokeResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setSmokeBusy(false);
    }
  };

  const active = data.active_provider;
  const stripeEnv = data.env_status.stripe;
  const hc = data.healthcheck || {};
  const lastSmoke = data.last_smoke_test;

  return (
    <div
      className="mt-4 rounded-xl border border-[#E5E0D8] bg-white p-4"
      data-testid="issuance-provider-tile"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${dotColor(active)}`} />
            <p className="text-sm font-medium text-[#112318]">Card-issuance provider</p>
            <span
              className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] mono"
              data-testid="issuance-active-provider"
            >
              {active}
            </span>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            {active === "mock" && "Closed-loop cards with locally-generated PANs. No external network."}
            {active === "stripe" && (
              hc.reachable
                ? "Stripe Issuing reachable. New cards will be provisioned on the Visa network."
                : hc.reachable === false
                  ? `Stripe Issuing unreachable: ${hc.error?.slice(0, 100) || "unknown error"}`
                  : "Stripe Issuing — checking reachability…"
            )}
            {active === "nium" && "Nium adapter (stub — implement before flipping to live traffic)."}
            {active === "wise" && "Wise adapter (stub — implement before flipping to live traffic)."}
          </p>
          {lastSmoke?.ts && (
            <p className="text-[11px] text-[#7A8B82] mt-1.5" data-testid="issuance-last-smoke">
              Last smoke test: {new Date(lastSmoke.ts).toLocaleString()} · {lastSmoke.result?.ok ? `✓ ${lastSmoke.provider} ${lastSmoke.result.last4}` : `✗ ${lastSmoke.result?.error?.slice(0, 60) || "failed"}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={smokeBusy}
            onClick={runSmokeTest}
            className="text-xs px-3 py-1.5 rounded-lg border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5 disabled:opacity-50"
            data-testid="issuance-smoke-test-btn"
          >
            {smokeBusy ? "Testing…" : "Run smoke test"}
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {data.supported.map((p) => (
          <button
            key={p}
            type="button"
            disabled={busy || p === active}
            onClick={() => switchProvider(p)}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${
              p === active
                ? "border-[#184A30] bg-[#184A30] text-white cursor-default"
                : "border-[#E5E0D8] text-[#4A5C50] hover:border-[#184A30]/40 hover:bg-[#184A30]/5"
            }`}
            data-testid={`issuance-switch-${p}`}
          >
            {p}
            {p === "stripe" && !stripeEnv.configured && " (no key)"}
          </button>
        ))}
      </div>

      {smokeResult && (
        <div
          className={`mt-3 rounded-lg p-3 text-xs ${smokeResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-rose-50 border border-rose-200 text-rose-900"}`}
          data-testid="issuance-smoke-result"
        >
          {smokeResult.ok ? (
            <>
              <div className="font-medium">✓ Smoke test passed via {smokeResult.provider}</div>
              <div className="mono mt-1">{smokeResult.masked_pan} · exp {smokeResult.expiry} · {smokeResult.network}</div>
              <div className="text-[11px] mt-1 opacity-80">Cleanup: {smokeResult.cleanup}</div>
            </>
          ) : (
            <>
              <div className="font-medium">✗ Smoke test failed</div>
              <div className="mt-1">{smokeResult.error}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
